import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import { Box } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { useUpdateUserRequestStatusMutation } from '@graphql/generated';
import theme, { SCALED_DIMENSIONS } from '@themes/theme';

import { RestyleProps } from '../Icon/RestyleIcon';

type BorderRadiusKey = keyof typeof theme.borderRadii;

interface CustomButtonProps {
  backgroundColor: RestyleProps['backgroundColor'];
  prefix?: React.ReactChild;
  onPress: () => void;
  variant: 'copy' | 'create';
  accessibilityLabel: string;
  suffix?: string;
  borderRadius?: BorderRadiusKey;
  height?: number;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  backgroundColor,
  prefix,
  onPress,
  variant,
  suffix,
  accessibilityLabel,
  borderRadius = 'l',
  height,
}) => {
  const { BUTTON_HEIGHT } = SCALED_DIMENSIONS;
  return (
    <Button
      backgroundColor={backgroundColor}
      prefix={prefix}
      variant={variant}
      onPress={onPress}
      isSmall
      centerIcon={!suffix}
      height={height ?? BUTTON_HEIGHT}
      borderRadius={borderRadius}
      borderColor='black'
      accessibilityLabel={accessibilityLabel}
      prefixMarginRight={Platform.OS === 'web' ? 'xs' : 'xxs'}
      children={suffix || ''}></Button>
  );
};

interface AcceptDeclineButtonsProps {
  requestId?: string;
  suffix?: boolean;
  acceptIconName?: RestyleProps['name'];
  declineIconName?: RestyleProps['name'];
  fromChatDetail?: boolean;
  toggleAcceptDecline?: () => void;
  borderRadius?: BorderRadiusKey;
  /** Overrides the default SCALED_DIMENSIONS.BUTTON_HEIGHT. */
  height?: number;
}

const AcceptDeclineButtons: React.FC<AcceptDeclineButtonsProps> = ({
  requestId,
  suffix,
  toggleAcceptDecline,
  acceptIconName = 'CheckCircle',
  declineIconName = 'X',
  fromChatDetail = false,
  borderRadius,
  height,
}) => {
  const { t } = useTranslation('models');

  const [updateUserRequestStatus] = useUpdateUserRequestStatusMutation();
  const onAcceptOrDecline = (status: string) => {
    let refetchQueries = [];
    if (fromChatDetail && status === 'rejected') {
      refetchQueries = ['listChatsPagination'];
    } else if (fromChatDetail) {
      refetchQueries = ['getChat', 'listMessages', 'listChatsPagination'];
    } else {
      refetchQueries = ['listMessages', 'listProjects', 'getProject'];
    }
    updateUserRequestStatus({
      variables: {
        id: requestId || '',
        status: status,
      },
      optimisticResponse: {
        updateUserRequestStatus: {
          __typename: 'UserRequest',
          id: requestId || '',
          status: status,
        },
      },
      update: (cache, { data }) => {
        const updated = data?.updateUserRequestStatus;
        if (!updated?.id) return;
        cache.evict({
          id: cache.identify({ __typename: 'UserRequest', id: updated.id }),
        });
        cache.gc();
      },
      refetchQueries: refetchQueries,
      onCompleted: () => {
        if (!fromChatDetail || status === 'rejected') {
          toggleAcceptDecline?.();
        }
      },
    });
  };
  return (
    <>
      <Box width={suffix ? 125 : 51} marginRight='xs'>
        <CustomButton
          backgroundColor='white'
          variant='copy'
          prefix={
            fromChatDetail ? undefined : (
              <Icon variant='s' name={declineIconName} color='black' />
            )
          }
          suffix={suffix ? 'Decline' : ''}
          onPress={() => onAcceptOrDecline('rejected')}
          accessibilityLabel={t('shared:decline')}
          borderRadius={borderRadius}
          height={height}
        />
      </Box>
      <Box width={suffix ? 125 : 51}>
        <CustomButton
          backgroundColor='black'
          variant='create'
          suffix={suffix ? 'Approve' : ''}
          prefix={<Icon variant='s' name={acceptIconName} color='white' />}
          onPress={() =>
            onAcceptOrDecline(fromChatDetail ? 'joined' : 'approved')
          }
          accessibilityLabel={t('shared:accept')}
          borderRadius={borderRadius}
          height={height}
        />
      </Box>
    </>
  );
};

export default AcceptDeclineButtons;
